import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ValueListItemView } from '@messe/shared'
import { Button } from './ui/button.js'
import { TextField } from './ui/field.js'
import { Alert } from './ui/feedback.js'
import { Dialog } from './ui/dialog.js'
import { format, t } from '../i18n/index.js'
import { ApiError } from '../lib/api.js'
import { listValueListItems, mergeValueListItems } from '../lib/value-lists.js'

interface Props {
  open: boolean
  listId: string
  /** The entry that will disappear. */
  source: ValueListItemView | null
  onClose: () => void
  onMerged: (message: string) => void
}

export function MergeDialog({ open, listId, source, onClose, onMerged }: Props) {
  const strings = t()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [targetId, setTargetId] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setSearch('')
    setTargetId(null)
    setFormError(null)
  }, [open, source])

  const candidates = useQuery({
    queryKey: ['merge-candidates', listId, search],
    queryFn: () => listValueListItems(listId, { search: search || undefined, limit: 20 }),
    enabled: open,
  })

  const merge = useMutation({
    mutationFn: () => {
      if (!source || targetId === null) throw new Error('No target selected')
      return mergeValueListItems(listId, source.id, targetId)
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['value-list-items'] })
      await queryClient.invalidateQueries({ queryKey: ['value-lists'] })
      await queryClient.invalidateQueries({ queryKey: ['pending-entries'] })
      onMerged(
        format(strings.valueLists.merge.success, {
          items: result.repointed.itemSupplier,
          attributes: result.repointed.itemAttributes,
          users: result.repointed.users,
        }),
      )
    },
    onError: (error) => {
      setFormError(
        error instanceof ApiError
          ? (error.problem.detail ?? strings.errors.unexpected)
          : strings.errors.unexpected,
      )
    },
  })

  // The entry being merged away must not appear as its own target.
  const options = (candidates.data?.items ?? []).filter((item) => item.id !== source?.id)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={strings.valueLists.merge.heading}
      description={
        source ? format(strings.valueLists.merge.description, { source: source.label }) : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {strings.valueLists.actions.cancel}
          </Button>
          <Button
            variant="danger"
            disabled={merge.isPending}
            onClick={() => {
              setFormError(null)
              if (targetId === null) {
                setFormError(strings.valueLists.merge.noTarget)
                return
              }
              merge.mutate()
            }}
          >
            {merge.isPending ? strings.valueLists.actions.saving : strings.valueLists.merge.submit}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {formError && <Alert tone="danger">{formError}</Alert>}
        <Alert tone="warning">{strings.valueLists.merge.warning}</Alert>

        <TextField
          label={strings.valueLists.merge.target}
          placeholder={strings.valueLists.merge.targetPlaceholder}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div
          className="border-border max-h-64 overflow-y-auto rounded-md border"
          role="radiogroup"
          aria-label={strings.valueLists.merge.targetList}
        >
          {options.map((item) => (
            <label
              key={item.id}
              className="border-border hover:bg-surface-sunken flex cursor-pointer items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0"
            >
              <input
                type="radio"
                name="merge-target"
                className="accent-accent"
                checked={targetId === item.id}
                onChange={() => setTargetId(item.id)}
              />
              <span className="flex-1">
                <span className="font-medium">{item.label}</span>
                <span className="text-text-muted ml-2 text-xs">{item.value}</span>
              </span>
              {item.usageCount > 0 && (
                <span className="text-text-muted text-xs">{item.usageCount}×</span>
              )}
            </label>
          ))}

          {options.length === 0 && (
            <p className="text-text-muted px-3 py-2 text-sm">{strings.valueLists.detail.empty}</p>
          )}
        </div>
      </div>
    </Dialog>
  )
}
